/* KpPanel – gemensam högerpanel enligt KanPlan DS Del Y (docs/design/kanplan-designsystem.md).

   Två lägen:
   - mode="detail" (standard): ingen gråtoning, sidan bakom går att använda (byta rad,
     filtrera). Stängs med X eller Esc – ALDRIG av klick utanför.
   - mode="form": lätt gråtoning under toppmenyn, scroll-lås och fokusfälla. Stängs med
     X, Esc eller klick på gråtoningen. Är dirty=true frågar panelen först.

   Tre bredder: size="sm" (420), "md" (560, standard), "lg" (820).

   Panelen renderas via portal till document.body och börjar under toppmenyn
   (top: var(--kp-header-h)). Medan den är öppen sätts klassen kp-panel-open på <html>
   så att toppmenyn läggs över panelen och dess notiser/menyer öppnas ovanpå.
   Klassen räknas per öppen panel och tas alltid bort vid stängning och unmount.

     <KpPanel open={!!selected} onClose={() => setSelected(null)}
       context={`#${selected.nr} · ${selected.project}`} title={selected.title}
       actions={<button className="kp-icon-btn" aria-label="Redigera">…</button>}
       meta={<StatusPill status={selected.status} />}
       tabs={<KpPanelTabs … />}>
       …innehåll…
     </KpPanel>
*/
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ConfirmDialog from '../dialog/ConfirmDialog';
import './KpPanel.css';

const SIZES = ['sm', 'md', 'lg'];
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Räknare så att toppmenyn ligger kvar ovanpå om två komponenter råkar vara monterade
// samtidigt (t.ex. under en övergång), och släpps först när den sista stängs.
let openCount = 0;
function markOpen() {
  openCount += 1;
  document.documentElement.classList.add('kp-panel-open');
}
function markClosed() {
  openCount = Math.max(0, openCount - 1);
  if (openCount === 0) document.documentElement.classList.remove('kp-panel-open');
}

// Stapel över öppna paneler i öppningsordning. Bara den översta (senast öppnade)
// hanterar Esc, så att ett Esc med två staplade paneler stänger en i taget.
const panelStack = [];
function pushPanel(token) { panelStack.push(token); }
function removePanel(token) {
  const i = panelStack.lastIndexOf(token);
  if (i !== -1) panelStack.splice(i, 1);
}
function isTopPanel(token) { return panelStack[panelStack.length - 1] === token; }

function IconX() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   title: React.ReactNode,
 *   context?: React.ReactNode,
 *   titleBadges?: React.ReactNode,
 *   actions?: React.ReactNode,
 *   meta?: React.ReactNode,
 *   tabs?: React.ReactNode,
 *   footer?: React.ReactNode | ((api: { requestClose: () => void }) => React.ReactNode),
 *   size?: 'sm'|'md'|'lg',
 *   mode?: 'detail'|'form',
 *   dirty?: boolean,
 *   className?: string,
 *   ariaLabel?: string,
 *   children?: React.ReactNode,
 * }} props
 */
export default function KpPanel({
  open, onClose, title, context, titleBadges, actions, meta, tabs, footer,
  size = 'md', mode = 'detail', dirty = false, className = '', ariaLabel, children,
}) {
  const panelRef = useRef(null);
  const titleRef = useRef(null);
  const returnFocusRef = useRef(null);
  const stackTokenRef = useRef({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const titleId = useId();
  const isForm = mode === 'form';
  const sz = SIZES.includes(size) ? size : 'md';

  // Stängning går alltid via requestClose, så att frågan om osparade ändringar inte kan kringgås.
  const requestClose = useCallback(() => {
    if (dirty) { setConfirmOpen(true); return; }
    onClose && onClose();
  }, [dirty, onClose]);

  // Toppmenyn ovanpå + fokus in/ut.
  useEffect(() => {
    if (!open) return undefined;
    markOpen();
    returnFocusRef.current = document.activeElement;
    const t = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      // Varje del i FOCUSABLE måste prefixas, annars träffar t.ex. "button" även huvudets knappar.
      // Prova fälten i tur och ordning: ett dolt fält tar inte emot fokus, och då ska nästa
      // prövas i stället för att fokus blir kvar bakom panelen (HP-16, EK-byggaren).
      // Inmatningsfält först, sedan övriga. Destruktiva knappar (klass med "danger") får
      // aldrig startfokus, så att Enter inte råkar radera något.
      const all = isForm ? Array.from(panel.querySelectorAll(FOCUSABLE.split(',').map((s) => `.kp-panel__body ${s.trim()}`).join(', '))) : [];
      const isField = (el) => /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) && el.type !== 'hidden';
      const isDanger = (el) => /danger/i.test(el.className || '');
      const candidates = [...all.filter(isField), ...all.filter((el) => !isField(el) && !isDanger(el))];
      const target = candidates.find((el) => { el.focus({ preventScroll: true }); return document.activeElement === el; });
      if (!target) (titleRef.current || panel).focus({ preventScroll: true });
    }, 0);
    return () => {
      window.clearTimeout(t);
      markClosed();
      const back = returnFocusRef.current;
      if (back && typeof back.focus === 'function' && document.contains(back)) back.focus({ preventScroll: true });
    };
  }, [open, isForm]);

  // Plats i stapeln medan panelen är öppen.
  useEffect(() => {
    if (!open) return undefined;
    const token = stackTokenRef.current;
    pushPanel(token);
    return () => removePanel(token);
  }, [open]);

  // Esc stänger bara den översta panelen och markeras som hanterad, så att inget annat
  // reagerar på samma tangenttryck. Inre komponenter (dropdowns, ConfirmDialog) som
  // hanterar Esc själva anropar preventDefault/stopPropagation och når då aldrig hit.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (!isTopPanel(stackTokenRef.current)) return;
      if (confirmOpen) return;
      e.preventDefault();
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirmOpen, requestClose]);

  // Formulärläge: scroll-lås på sidan bakom.
  useEffect(() => {
    if (!open || !isForm) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, isForm]);

  // Formulärläge: fokusfälla (Tab stannar i panelen).
  const onPanelKeyDown = useCallback((e) => {
    if (!isForm || e.key !== 'Tab' || !panelRef.current) return;
    const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, [isForm]);

  if (!open || typeof document === 'undefined') return null;

  const cls = ['kp-panel', `kp-panel--${sz}`, isForm ? 'kp-panel--form' : '', className].filter(Boolean).join(' ');

  return createPortal(
    <>
      {isForm && (
        <div className="kp-panel-backdrop" data-testid="kp-panel-backdrop" onClick={requestClose} aria-hidden="true" />
      )}
      <aside
        ref={panelRef}
        className={cls}
        role="dialog"
        aria-modal={isForm ? 'true' : 'false'}
        aria-labelledby={ariaLabel ? undefined : titleId}
        aria-label={ariaLabel}
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
      >
        <header className={`kp-panel__head${tabs ? ' has-tabs' : ''}`}>
          {context && <div className="kp-panel__context">{context}</div>}
          <div className="kp-panel__titlerow">
            <h2 className="kp-panel__title" id={titleId} ref={titleRef} tabIndex={-1}>
              {title}
              {titleBadges}
            </h2>
            <div className="kp-panel__actions">
              {actions}
              {actions && <span className="kp-panel__sep" aria-hidden="true" />}
              <button type="button" className="kp-icon-btn" onClick={requestClose} aria-label="Stäng" title="Stäng">
                <IconX />
              </button>
            </div>
          </div>
          {meta && <div className="kp-panel__meta">{meta}</div>}
          {tabs && <div className="kp-panel__tabs">{tabs}</div>}
        </header>
        <div className="kp-panel__body">{children}</div>
        {footer && <footer className="kp-panel__foot">{typeof footer === 'function' ? footer({ requestClose }) : footer}</footer>}
      </aside>
      <ConfirmDialog
        open={confirmOpen}
        title="Osparade ändringar"
        message="Du har ändringar som inte är sparade. Vill du stänga panelen ändå? Ändringarna försvinner."
        confirmLabel="Stäng utan att spara"
        cancelLabel="Fortsätt redigera"
        variant="danger"
        onConfirm={() => { setConfirmOpen(false); onClose && onClose(); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>,
    document.body,
  );
}

/** Flikrad för panelhuvudet (DS Del B7 variant D). */
export function KpPanelTabs({ tabs, value, onChange, ariaLabel = 'Flikar' }) {
  return (
    <div className="kp-panel__tablist" role="tablist" aria-label={ariaLabel}>
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          role="tab"
          aria-selected={value === t.value}
          className={`kp-panel__tab${value === t.value ? ' is-active' : ''}`}
          onClick={() => onChange(t.value)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
